import { useEffect, useState } from 'react';
import { MainHeader } from './MainHeader';
import { Footer } from './Footer';
import { getCmsPage, type CmsPage, type CmsUserType } from '../../lib/professional-api';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';

interface CmsPageViewProps {
  code: 'TERMS_CONDITIONS' | 'PRIVACY_POLICY' | 'ABOUT_US' | 'CANCELLATION_POLICY';
  userType?: CmsUserType;
  fallbackTitle: string;
}

export function CmsPageView({ code, userType = 'CUSTOMER', fallbackTitle }: CmsPageViewProps) {
  const [page, setPage] = useState<CmsPage | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCmsPage(code, userType)
      .then((res) => {
        if (!cancelled) setPage(res.data);
      })
      .catch((err) => {
        if (!cancelled) toast.error(apiErrorMessage(err, 'Could not load content.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code, userType]);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />
      <main className="flex-1 py-12 px-4">
        <div className="max-w-[820px] mx-auto">
          {loading && (
            <div className="text-center text-[#908e8e] py-16">Loading…</div>
          )}
          {!loading && page && (
            <article
              className="cms-content text-[#3a3335] text-[15px] leading-[24px]"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />
          )}
          {!loading && !page && (
            <h1 className="text-center font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[28px]">
              {fallbackTitle}
            </h1>
          )}
        </div>
      </main>
      <Footer />

      <style>{`
        .cms-content h1 { font-family: 'Playfair Display', sans-serif; font-weight: 800; color: #8e2a2f; font-size: 32px; line-height: 40px; margin: 0 0 16px; }
        .cms-content h2 { font-family: 'Playfair Display', sans-serif; font-weight: 700; color: #3a3335; font-size: 22px; line-height: 30px; margin: 24px 0 10px; }
        .cms-content h3 { font-weight: 700; color: #3a3335; font-size: 17px; margin: 18px 0 8px; }
        .cms-content p { margin: 0 0 12px; }
        .cms-content ul, .cms-content ol { padding-left: 22px; margin: 0 0 14px; }
        .cms-content li { margin-bottom: 6px; }
        .cms-content a { color: #8e2a2f; text-decoration: underline; }
        .cms-content strong { color: #3a3335; font-weight: 700; }
        .cms-content blockquote { border-left: 3px solid #8e2a2f; padding-left: 14px; color: #555; margin: 16px 0; font-style: italic; }
        .cms-content code { background: #f4f4f4; padding: 2px 6px; border-radius: 4px; font-size: 14px; }
      `}</style>
    </div>
  );
}
